/**
 * 플랜 공개 범위 (PRD2 §5.3.4).
 * 비공개는 소유자·관리자 목록에 열쇠로 보이고, 공개는 참여자가 읽기만 한다.
 */

import { useSyncExternalStore } from "react";
import { canWorkspace, type WorkspaceRole } from "./workspace-access";

export type PlanVisibility = "private" | "workspace";

const KEY = "plan-visibility-v1";

let map: Record<string, PlanVisibility> = read();
let version = 0;
const listeners = new Set<() => void>();

function read(): Record<string, PlanVisibility> {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    const next: Record<string, PlanVisibility> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (value === "workspace" || value === "private") next[id] = value;
    }
    return next;
  } catch {
    return {};
  }
}

function emit() {
  version += 1;
  if (typeof localStorage !== "undefined") localStorage.setItem(KEY, JSON.stringify(map));
  for (const listener of listeners) listener();
}

export function resetPlanVisibility(): void {
  map = {};
  if (typeof localStorage !== "undefined") localStorage.removeItem(KEY);
  emit();
}

export function planVisibilityOf(id: string | null | undefined): PlanVisibility {
  if (!id) return "private";
  return map[id] ?? "private";
}

export function setPlanVisibility(id: string, value: PlanVisibility): void {
  if (!id) return;
  map = { ...map, [id]: value };
  emit();
}

export function subscribePlanVisibility(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePlanVisibilityTick(): number {
  return useSyncExternalStore(subscribePlanVisibility, () => version, () => version);
}

export function usePlanVisibility(id: string | null | undefined): PlanVisibility {
  usePlanVisibilityTick();
  return planVisibilityOf(id);
}

/** 목록에 보일지, 제목에 열쇠를 둘지. 게스트 목록은 따로 거른다. */
export function showInPlanList(visibility: PlanVisibility, role: WorkspaceRole): { shown: boolean; locked: boolean } {
  const locked = visibility === "private";
  if (role === "guest") return { shown: true, locked };
  if (locked) return { shown: canWorkspace(role, "plan.meta.readAll"), locked: true };
  return { shown: true, locked: false };
}

/** 공개 플랜을 멤버가 아닌 참여자가 볼 때. 읽기만 하고 금액은 없다. */
export function readsPublicPlan(visibility: PlanVisibility, role: WorkspaceRole): boolean {
  return visibility === "workspace" && role !== "owner" && role !== "admin" && role !== "guest";
}
